import { useEffect, useState } from 'react'
import { LoadingSpinner } from './LoadingSpinner'

// Staged progress for the homepage demo `creating` phase (PRD-GrowthLoops 1.1).
// Client-side staging keyed to the wizard call lifecycle: stages advance on a
// timer while the request is in flight; the final stage stays active until the
// wizard response flips the phase to `building` (which unmounts this
// component). No fake percentages — just honest stages + elapsed time.
export const WIZARD_STAGES = [
  'Analyzing your idea',
  'Picking a template',
  'Creating your project',
  'Starting your agent',
]

// Seconds of elapsed time at which each stage becomes the active one. The last
// stage is pinned: it never completes client-side.
const STAGE_START_AT = [0, 4, 9, 15]

export function WizardProgress({ prompt }: { prompt: string }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(timer)
  }, [])

  let active = 0
  for (let i = 0; i < STAGE_START_AT.length; i++) {
    if (elapsed >= STAGE_START_AT[i]) active = i
  }

  const elapsedLabel = `${Math.floor(elapsed / 60)}:${(elapsed % 60).toString().padStart(2, '0')}`

  return (
    <div className="text-center">
      <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">Your AI team is spinning up...</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">&ldquo;{prompt}&rdquo;</p>

      <ul className="inline-block text-left space-y-3 mb-6">
        {WIZARD_STAGES.map((stage, i) => (
          <li key={stage} className="flex items-center gap-3 text-sm">
            {i < active ? (
              <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-label="done">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            ) : i === active ? (
              <LoadingSpinner size="sm" className="text-primary-500 shrink-0" />
            ) : (
              <span className="w-5 h-5 shrink-0 rounded-full border-2 border-gray-200 dark:border-gray-700" />
            )}
            <span className={i <= active ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400 dark:text-gray-500'}>
              {stage}
            </span>
          </li>
        ))}
      </ul>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        {elapsedLabel} elapsed &mdash; builds typically take 2&ndash;5 minutes
      </p>
    </div>
  )
}
